/**
 * PortalRH — Page candidat (publique, hors OAuth).
 *
 * 3 étapes : info → OTP → dashboard upload.
 * Identité de marque : palette teal/cream Ô P'tites Quenottes.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import {
  FileText, Mail, Loader, ShieldCheck, Upload, Check, X, AlertCircle, Clock,
  Calendar, MapPin, Phone, Globe, Building2, RefreshCw, LogOut,
} from 'lucide-react';

const portal = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  timeout: 30000,
  withCredentials: true,
});

const STATUS_LABELS = {
  pending:   { label: 'À fournir',  bg: 'bg-stone-100', text: 'text-stone-700', icon: Clock },
  uploaded:  { label: 'Envoyé',     bg: 'bg-amber-100', text: 'text-amber-800', icon: Check },
  validated: { label: 'Validé',     bg: 'bg-emerald-100', text: 'text-emerald-700', icon: ShieldCheck },
  rejected:  { label: 'Refusé',     bg: 'bg-red-100',   text: 'text-red-700',   icon: X },
};

const BRAND = {
  primary: 'bg-teal-700',
  primaryHover: 'hover:bg-teal-800',
  primaryText: 'text-teal-700',
  ring: 'focus:ring-teal-300',
  accentBg: 'bg-amber-50',
  accentBorder: 'border-amber-200',
  pageBg: 'bg-stone-50',
  cardBg: 'bg-white',
  softShadow: 'shadow-sm',
};

// Textes affichés sur le portail, adaptés au type de demande
function getPurposeStrings(purpose, metadata) {
  const monthLabel = metadata?.monthLabel || '';
  switch (purpose) {
    case 'transport_monthly':
      return {
        badge: `Justificatif de transport${monthLabel ? ` — ${monthLabel}` : ''}`,
        introTitle: 'Bonjour',
        introBody1: `Comme chaque mois, merci de bien vouloir nous transmettre votre justificatif de transport${monthLabel ? ` pour ${monthLabel}` : ''}.`,
        introBody2: `Pour des raisons de sécurité, vous allez recevoir un code de vérification à 6 chiffres par email. Il sera valable 10 minutes.`,
        dashboardSubtitle: monthLabel
          ? `Téléversez votre justificatif de transport pour ${monthLabel}.`
          : `Téléversez votre justificatif de transport.`,
      };
    case 'misc':
      return {
        badge: 'Document à transmettre',
        introTitle: 'Bonjour',
        introBody1: `Nous avons besoin d'un document complémentaire pour mettre à jour votre dossier.`,
        introBody2: `Pour des raisons de sécurité, vous allez recevoir un code de vérification à 6 chiffres par email. Il sera valable 10 minutes.`,
        dashboardSubtitle: `Voici la pièce à nous transmettre.`,
      };
    case 'embauche':
    default:
      return {
        badge: `Constitution de votre dossier d'embauche`,
        introTitle: 'Bonjour',
        introBody1: `Suite à votre promesse d'embauche, nous vous remercions de bien vouloir transmettre quelques documents nécessaires à la constitution de votre dossier.`,
        introBody2: `Pour des raisons de sécurité, vous allez recevoir un code de vérification à 6 chiffres par email. Il sera valable 10 minutes.`,
        dashboardSubtitle: `Voici la liste des pièces à nous transmettre. Vous pouvez les envoyer en plusieurs fois, à votre rythme.`,
      };
  }
}

export default function PortalRH() {
  const token = (window.location.pathname.match(/^\/portal-rh\/([^/?#]+)/) || [])[1] || '';

  const [step, setStep] = useState('loading');
  const [info, setInfo] = useState(null);
  const [me, setMe] = useState(null);
  const [otpSending, setOtpSending] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [otpEmailMasked, setOtpEmailMasked] = useState(null);
  const [error, setError] = useState(null);
  const [uploadingItemId, setUploadingItemId] = useState(null);

  useEffect(() => {
    if (!token) { setStep('invalid'); return; }
    portal.get(`/portal/document-requests/${token}/info`)
      .then((r) => {
        setInfo(r.data);
        return portal.get('/portal/document-requests/me')
          .then((r2) => { setMe(r2.data); setStep('dashboard'); })
          .catch(() => { setStep('info'); });
      })
      .catch((e) => {
        setError(e.response?.data?.error || 'Lien invalide ou expiré');
        setStep('invalid');
      });
  }, [token]);

  const handleSendOtp = async () => {
    setError(null);
    setOtpSending(true);
    try {
      const { data } = await portal.post(`/portal/document-requests/${token}/request-otp`);
      setOtpEmailMasked(data.emailMasked);
      setStep('otp');
    } catch (e) {
      setError(e.response?.data?.error || 'Erreur envoi code');
    } finally { setOtpSending(false); }
  };

  const handleVerifyOtp = async (e) => {
    e?.preventDefault?.();
    setError(null);
    if (!/^\d{6}$/.test(otpCode)) { setError('Le code doit contenir 6 chiffres'); return; }
    setOtpVerifying(true);
    try {
      await portal.post(`/portal/document-requests/${token}/verify-otp`, { code: otpCode });
      const { data } = await portal.get('/portal/document-requests/me');
      setMe(data);
      setStep('dashboard');
    } catch (err) {
      setError(err.response?.data?.error || 'Code incorrect');
    } finally { setOtpVerifying(false); }
  };

  const refreshMe = async () => {
    try { const { data } = await portal.get('/portal/document-requests/me'); setMe(data); }
    catch (e) {
      if (e.response?.status === 401) {
        setError(e.response?.data?.error || 'Session expirée, demande un nouveau code.');
        setMe(null);
        setOtpCode('');
        setStep('info');
      }
    }
  };

  const handleLogout = useCallback(async (reason = 'manual') => {
    try { await portal.post('/portal/document-requests/me/logout'); } catch {}
    setMe(null);
    setOtpCode('');
    setError(reason === 'idle' ? 'Session fermée après 15 minutes d\'inactivité. Demande un nouveau code pour continuer.' : null);
    setStep('info');
  }, []);

  // Auto-déconnexion à 15 min d'inactivité (sliding window aligné backend)
  // Recense les activités UI (souris/clavier/scroll/touch) puis vérifie toutes les 30s.
  const lastActivityRef = useRef(Date.now());
  useEffect(() => {
    if (step !== 'dashboard') return;
    const mark = () => { lastActivityRef.current = Date.now(); };
    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
    events.forEach((e) => window.addEventListener(e, mark, { passive: true }));
    const intv = setInterval(() => {
      const idleMs = Date.now() - lastActivityRef.current;
      if (idleMs > 15 * 60 * 1000) {
        handleLogout('idle');
      }
    }, 30 * 1000);
    return () => {
      clearInterval(intv);
      events.forEach((e) => window.removeEventListener(e, mark));
    };
  }, [step, handleLogout]);

  const handleUpload = async (itemId, file) => {
    if (!file) return;
    setError(null);
    setUploadingItemId(itemId);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await portal.post(`/portal/document-requests/me/items/${itemId}/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      await refreshMe();
    } catch (e) {
      if (e.response?.status === 401) {
        setError(e.response?.data?.error || 'Session expirée, demande un nouveau code.');
        setMe(null);
        setStep('info');
      } else {
        setError(e.response?.data?.error || 'Erreur upload');
      }
    } finally { setUploadingItemId(null); }
  };

  // ─── Loading & erreur ────────────────────────────────────────────────

  if (step === 'loading') {
    return (
      <FullScreen>
        <Loader className="animate-spin text-stone-400" size={32} />
        <p className="mt-4 text-sm text-stone-500">Vérification du lien...</p>
      </FullScreen>
    );
  }

  if (step === 'invalid') {
    return (
      <FullScreen>
        <div className="w-full max-w-md bg-white rounded-2xl shadow p-8 text-center">
          <AlertCircle className="mx-auto mb-4 text-red-500" size={40} />
          <h1 className="text-lg font-semibold text-stone-800 mb-2">Lien invalide</h1>
          <p className="text-sm text-stone-600">
            {error || 'Ce lien est invalide ou a expiré. Si tu as reçu un email récemment, recharge la page. Sinon contacte la personne qui te l\'a envoyé.'}
          </p>
        </div>
      </FullScreen>
    );
  }

  // ─── Etapes avec branding ────────────────────────────────────────────

  const sender = step === 'dashboard' ? me?.sender : info?.sender;
  const employee = step === 'dashboard' ? me?.employee : info?.employee;
  const purpose = (step === 'dashboard' ? me?.purpose : info?.purpose) || 'embauche';
  const metadata = step === 'dashboard' ? me?.metadata : info?.metadata;
  const strings = getPurposeStrings(purpose, metadata);

  return (
    <div className={`min-h-screen ${BRAND.pageBg} flex flex-col`}>
      <BrandHeader sender={sender} />

      <main className="flex-1 max-w-3xl w-full mx-auto px-5 py-8">
        {step === 'info' && (
          <IntroCard
            employee={employee}
            sender={sender}
            tokenExpiresAt={info?.tokenExpiresAt}
            onSendOtp={handleSendOtp}
            otpSending={otpSending}
            error={error}
            strings={strings}
          />
        )}

        {step === 'otp' && (
          <OtpCard
            emailMasked={otpEmailMasked || info?.recipientEmailMasked}
            code={otpCode}
            setCode={setOtpCode}
            onSubmit={handleVerifyOtp}
            onResend={handleSendOtp}
            verifying={otpVerifying}
            sending={otpSending}
            error={error}
          />
        )}

        {step === 'dashboard' && (
          <Dashboard
            me={me}
            error={error}
            uploadingItemId={uploadingItemId}
            onUpload={handleUpload}
            onLogout={() => handleLogout('manual')}
            strings={strings}
          />
        )}
      </main>

      <BrandFooter sender={sender} />
    </div>
  );
}

// ─── Header avec logo + nom structure + tagline ─────────────────────────

function BrandHeader({ sender }) {
  const structure = sender?.structure;
  const logoUrl = structure?.logoUrl;
  // Affichage principal = Nom complet (fullName), avec fallback commercialName puis name
  const displayName = structure?.fullName || structure?.commercialName || structure?.name || "Ô P'tites Quenottes";
  const [logoFailed, setLogoFailed] = useState(false);

  return (
    <header className="bg-white border-b border-stone-200">
      <div className="max-w-3xl mx-auto px-5 py-4 flex items-center gap-4">
        {logoUrl && !logoFailed ? (
          <img
            src={logoUrl}
            alt={displayName}
            className="w-12 h-12 rounded-lg object-contain bg-stone-50 p-1 flex-shrink-0"
            onError={() => setLogoFailed(true)}
          />
        ) : (
          <div className="w-12 h-12 rounded-lg bg-teal-50 flex items-center justify-center flex-shrink-0">
            <Building2 size={22} className="text-teal-700" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="text-base font-semibold text-stone-800 truncate">{displayName}</div>
        </div>
      </div>
    </header>
  );
}

// ─── Carte intro (étape 1) ──────────────────────────────────────────────

function IntroCard({ employee, sender, tokenExpiresAt, onSendOtp, otpSending, error, strings }) {
  const structure = sender?.structure;
  return (
    <div className="grid md:grid-cols-3 gap-5">
      <div className="md:col-span-2">
        <div className={`${BRAND.cardBg} rounded-2xl ${BRAND.softShadow} p-7`}>
          <div className="flex items-center gap-3 mb-1">
            <span className="inline-block px-2.5 py-1 rounded-full bg-teal-50 text-teal-800 text-xs font-medium">
              {strings.badge}
            </span>
          </div>
          <h1 className="text-2xl font-semibold text-stone-800 mt-2">
            {strings.introTitle} <span className="text-teal-700">{employee?.firstName}</span>,
          </h1>
          <p className="text-sm text-stone-600 mt-3 leading-relaxed">
            {strings.introBody1}
          </p>
          <p className="text-sm text-stone-600 mt-2 leading-relaxed">
            Pour des raisons de sécurité, vous allez recevoir un <strong>code de vérification à 6 chiffres</strong> par email. Il sera valable 10 minutes.
          </p>

          {error && <ErrorMsg className="mt-4">{error}</ErrorMsg>}

          <button
            onClick={onSendOtp}
            disabled={otpSending}
            className={`mt-6 w-full sm:w-auto px-6 py-3 ${BRAND.primary} ${BRAND.primaryHover} text-white rounded-lg disabled:opacity-60 flex items-center justify-center gap-2 text-sm font-medium`}
          >
            {otpSending ? <Loader size={14} className="animate-spin" /> : <Mail size={14} />}
            {otpSending ? 'Envoi du code...' : 'Recevoir le code par email'}
          </button>

          {tokenExpiresAt && (
            <p className="mt-4 text-xs text-stone-500 flex items-center gap-1.5">
              <Calendar size={12} /> Lien valable jusqu'au {new Date(tokenExpiresAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
            </p>
          )}
        </div>
      </div>

      <SenderCard sender={sender} />
    </div>
  );
}

// ─── Carte OTP ──────────────────────────────────────────────────────────

function OtpCard({ emailMasked, code, setCode, onSubmit, onResend, verifying, sending, error }) {
  return (
    <div className={`${BRAND.cardBg} rounded-2xl ${BRAND.softShadow} p-8 max-w-md mx-auto`}>
      <div className="text-center mb-6">
        <div className="inline-flex w-14 h-14 rounded-full bg-teal-50 items-center justify-center mb-3">
          <ShieldCheck size={26} className="text-teal-700" />
        </div>
        <h1 className="text-xl font-semibold text-stone-800">Code envoyé</h1>
        <p className="text-sm text-stone-600 mt-2">
          Saisissez le code à 6 chiffres reçu à <strong>{emailMasked}</strong>. Il est valable 10 minutes.
        </p>
      </div>
      <form onSubmit={onSubmit}>
        <input
          type="text"
          inputMode="numeric"
          autoFocus
          autoComplete="one-time-code"
          maxLength={6}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          className="w-full text-center text-3xl tracking-[0.6em] font-mono font-semibold border border-stone-300 rounded-lg py-3 mb-4 focus:outline-none focus:ring-2 focus:ring-teal-300"
          placeholder="••••••"
        />
        {error && <ErrorMsg>{error}</ErrorMsg>}
        <button
          type="submit"
          disabled={verifying || code.length !== 6}
          className={`w-full py-3 ${BRAND.primary} ${BRAND.primaryHover} text-white rounded-lg disabled:opacity-60 flex items-center justify-center gap-2 text-sm font-medium`}
        >
          {verifying ? <Loader size={14} className="animate-spin" /> : <Check size={14} />}
          {verifying ? 'Vérification...' : 'Valider le code'}
        </button>
      </form>
      <button
        onClick={onResend}
        disabled={sending}
        className="w-full mt-3 text-xs text-teal-700 hover:underline disabled:opacity-50 flex items-center justify-center gap-1.5"
      >
        <RefreshCw size={11} /> {sending ? 'Envoi...' : 'Renvoyer un nouveau code'}
      </button>
    </div>
  );
}

// ─── Dashboard upload ──────────────────────────────────────────────────

function Dashboard({ me, error, uploadingItemId, onUpload, onLogout, strings }) {
  const requiredItems = me?.items?.filter((i) => i.required) || [];
  const optionalItems = me?.items?.filter((i) => !i.required) || [];

  return (
    <>
      <div className={`${BRAND.cardBg} rounded-2xl ${BRAND.softShadow} p-6 mb-5`}>
        <div className="flex items-start gap-4">
          <div className="flex-1 min-w-0">
            <span className="inline-block px-2.5 py-1 rounded-full bg-teal-50 text-teal-800 text-xs font-medium mb-2">
              {strings.badge}
            </span>
            <h1 className="text-xl font-semibold text-stone-800">
              Bonjour {me?.employee?.firstName} {me?.employee?.lastName}
            </h1>
            <p className="text-sm text-stone-600 mt-1.5">
              {strings.dashboardSubtitle}
            </p>
            <p className="text-[11px] text-stone-400 mt-2 flex items-center gap-1">
              <ShieldCheck size={11} /> Session sécurisée — fermée automatiquement après 15 minutes d'inactivité.
            </p>
          </div>
          <div className="flex flex-col items-end gap-2 flex-shrink-0">
            <Progress me={me} />
            <button
              onClick={onLogout}
              className="text-xs text-stone-500 hover:text-stone-700 flex items-center gap-1.5 px-2 py-1 rounded hover:bg-stone-100"
              title="Quitter la session de manière sécurisée"
            >
              <LogOut size={12} /> Quitter
            </button>
          </div>
        </div>
      </div>

      {error && <ErrorMsg>{error}</ErrorMsg>}

      {/* Cas où il n'y a qu'un seul item : pas besoin de section/titre */}
      {(me?.items?.length === 1) && (
        <div className="space-y-3">
          {me.items.map((it) => (
            <DocItem key={it.id} item={it} uploading={uploadingItemId === it.id} onUpload={(f) => onUpload(it.id, f)} />
          ))}
        </div>
      )}

      {(me?.items?.length > 1) && (
        <>
          {requiredItems.length > 0 && (
            <Section title="Documents obligatoires" badge={`${requiredItems.length}`}>
              {requiredItems.map((it) => (
                <DocItem key={it.id} item={it} uploading={uploadingItemId === it.id} onUpload={(f) => onUpload(it.id, f)} />
              ))}
            </Section>
          )}

          {optionalItems.length > 0 && (
            <Section title="Documents complémentaires" badge={`${optionalItems.length}`}>
              {optionalItems.map((it) => (
                <DocItem key={it.id} item={it} uploading={uploadingItemId === it.id} onUpload={(f) => onUpload(it.id, f)} />
              ))}
            </Section>
          )}
        </>
      )}

      <p className="mt-8 text-center text-xs text-stone-400">
        Formats acceptés : PDF, JPG, PNG · 10 Mo maximum.<br />
        Tous les documents sont transmis de manière sécurisée et confidentielle.
      </p>
    </>
  );
}

function Section({ title, badge, children }) {
  return (
    <section className="mb-6">
      <div className="flex items-center gap-2 mb-3 px-1">
        <h2 className="text-sm font-semibold text-stone-700 uppercase tracking-wide">{title}</h2>
        {badge && <span className="text-xs bg-stone-200 text-stone-700 rounded-full px-2 py-0.5">{badge}</span>}
      </div>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function Progress({ me }) {
  if (!me?.items) return null;
  const total = me.items.length;
  const ok = me.items.filter((i) => i.status === 'validated' || i.status === 'uploaded').length;
  const pct = total > 0 ? Math.round((ok / total) * 100) : 0;
  return (
    <div className="hidden sm:block flex-shrink-0 w-32">
      <div className="text-xs text-stone-500 mb-1 text-right">{ok}/{total} transmis</div>
      <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
        <div className="h-full bg-teal-600 transition-all" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function DocItem({ item, uploading, onUpload }) {
  const fileRef = useRef(null);
  const sb = STATUS_LABELS[item.status] || STATUS_LABELS.pending;
  const Icon = sb.icon;
  const showUpload = item.status === 'pending' || item.status === 'rejected';

  return (
    <div className={`${BRAND.cardBg} rounded-xl ${BRAND.softShadow} border ${item.status === 'rejected' ? 'border-red-200' : 'border-stone-100'} p-4`}>
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-stone-800">{item.typeName}</span>
          </div>
          {item.category && <div className="text-xs text-stone-400 mt-0.5">{item.category}</div>}
          {item.rejectedReason && (
            <div className="mt-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2">
              <span className="font-medium">Document refusé : </span>{item.rejectedReason}
            </div>
          )}
          {item.status === 'uploaded' && (
            <div className="mt-1.5 text-xs text-amber-700">En attente de validation par l'administration.</div>
          )}
          {item.status === 'validated' && (
            <div className="mt-1.5 text-xs text-emerald-700">Document validé. Merci !</div>
          )}
        </div>
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${sb.bg} ${sb.text} flex-shrink-0`}>
          <Icon size={12} /> {sb.label}
        </span>
      </div>

      {showUpload && (
        <div className="mt-3">
          <input
            ref={fileRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
            className="hidden"
            disabled={uploading}
            onChange={(e) => onUpload(e.target.files?.[0])}
          />
          <button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className={`w-full py-2.5 text-sm ${BRAND.primary} ${BRAND.primaryHover} text-white rounded-lg disabled:opacity-60 flex items-center justify-center gap-2`}
          >
            {uploading ? <Loader size={14} className="animate-spin" /> : <Upload size={14} />}
            {uploading ? 'Transmission...' : (item.status === 'rejected' ? 'Renvoyer le document' : 'Téléverser le document')}
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Carte expéditeur (sidebar) ─────────────────────────────────────────

function SenderCard({ sender }) {
  if (!sender?.structure) return null;
  const s = sender.structure;
  const legal = sender.legalEmployer;
  return (
    <div className={`${BRAND.cardBg} rounded-2xl ${BRAND.softShadow} p-5 text-sm h-fit`}>
      <div className="text-xs text-stone-500 uppercase tracking-wide mb-3 font-medium">Demande envoyée par</div>
      <div className="font-semibold text-stone-800 mb-2">{s.fullName || s.commercialName || s.name}</div>
      <ul className="space-y-2 text-stone-600 text-xs">
        {s.address && (
          <li className="flex items-start gap-2">
            <MapPin size={12} className="mt-0.5 text-stone-400 flex-shrink-0" />
            <span>{s.address}</span>
          </li>
        )}
        {s.phone && (
          <li className="flex items-start gap-2">
            <Phone size={12} className="mt-0.5 text-stone-400 flex-shrink-0" />
            <span>{s.phone}</span>
          </li>
        )}
        {s.email && (
          <li className="flex items-start gap-2">
            <Mail size={12} className="mt-0.5 text-stone-400 flex-shrink-0" />
            <a href={`mailto:${s.email}`} className="hover:text-teal-700 break-all">{s.email}</a>
          </li>
        )}
        {s.website && (
          <li className="flex items-start gap-2">
            <Globe size={12} className="mt-0.5 text-stone-400 flex-shrink-0" />
            <a href={`https://${s.website.replace(/^https?:\/\//, '')}`} target="_blank" rel="noopener noreferrer" className="hover:text-teal-700 break-all">{s.website}</a>
          </li>
        )}
      </ul>
      {legal?.legalName && (
        <div className="mt-4 pt-3 border-t border-stone-100 text-[11px] text-stone-500">
          <div className="text-stone-400">Employeur juridique</div>
          <div className="text-stone-700 font-medium mt-0.5">{legal.legalName}</div>
          {legal.directorName && <div>Représenté par {legal.directorName}</div>}
        </div>
      )}
    </div>
  );
}

// ─── Pied de page ──────────────────────────────────────────────────────

function BrandFooter({ sender }) {
  const legal = sender?.legalEmployer;
  return (
    <footer className="border-t border-stone-200 bg-white">
      <div className="max-w-3xl mx-auto px-5 py-5 text-center text-xs text-stone-500">
        {legal?.legalName && <div className="font-medium text-stone-600">{legal.legalName}</div>}
        <div className="mt-1">Vos données sont traitées de manière confidentielle, dans le respect du RGPD.</div>
      </div>
    </footer>
  );
}

// ─── Utils ─────────────────────────────────────────────────────────────

function FullScreen({ children }) {
  return (
    <div className="min-h-screen bg-stone-50 flex items-center justify-center p-5">
      <div className="flex flex-col items-center w-full">{children}</div>
    </div>
  );
}

function ErrorMsg({ children, className = '' }) {
  return (
    <div className={`text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg p-2.5 flex items-start gap-2 ${className}`}>
      <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
      <span>{children}</span>
    </div>
  );
}
